// Memoized ChartSection.js to prevent excessive re-renders
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Space, Switch } from 'antd';
import Chart from 'chart.js/auto';
import MetricSelector from './MetricSelector';
import TimeGranularity from './TimeGranularity';
import { aggregateDataByGranularity, calculateAverages, chartColors } from './chartUtils';
import { startOfDay, startOfWeek, startOfMonth, format, parseISO } from 'date-fns';

// Using React.memo to prevent unnecessary re-renders of the whole component
const ChartSection = React.memo(({
                                     data,
                                     availableMetrics,
                                     selectedMetrics: propSelectedMetrics = [],
                                     onMetricAdd,
                                     onMetricRemove,
                                     onMetricToggleAverage,
                                     granularity: propGranularity = 'day',
                                     onGranularityChange,
                                     presetConfig
                                 }) => {
    // First render flag to avoid unnecessary state updates on mount
    const isFirstRender = useRef(true);
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const chartUpdateNeeded = useRef(false);

    // Local state for uncontrolled mode (backward compatibility)
    const [localSelectedMetrics, setLocalSelectedMetrics] = useState([]);
    const [localGranularity, setLocalGranularity] = useState('day');
    const [showAllAverages, setShowAllAverages] = useState(false);

    // Determine if component is controlled or uncontrolled
    const isControlled = !!onMetricAdd && !!onMetricRemove;

    // Get the actual values to use (either from props or local state)
    const selectedMetrics = isControlled ? propSelectedMetrics : localSelectedMetrics;
    const granularity = isControlled ? propGranularity : localGranularity;

    // Initialize from props or presetConfig
    useEffect(() => {
        if (isFirstRender.current) {
            // Initialize from props first, then fallback to presetConfig
            if (propSelectedMetrics.length > 0) {
                setLocalSelectedMetrics(propSelectedMetrics);
            } else if (presetConfig?.metrics) {
                setLocalSelectedMetrics(presetConfig.metrics);
            }

            if (propGranularity) {
                setLocalGranularity(propGranularity);
            } else if (presetConfig?.granularity) {
                setLocalGranularity(presetConfig.granularity);
            }

            isFirstRender.current = false;
        }
    }, [propSelectedMetrics, propGranularity, presetConfig]);

    // Update local state when props change (for controlled mode)
    useEffect(() => {
        if (!isFirstRender.current && isControlled) {
            setLocalSelectedMetrics(propSelectedMetrics);
        }
    }, [propSelectedMetrics, isControlled]);

    useEffect(() => {
        if (!isFirstRender.current && isControlled) {
            setLocalGranularity(propGranularity);
        }
    }, [propGranularity, isControlled]);

    // Event handlers that respect controlled/uncontrolled mode
    const handleMetricAdd = useMemo(() => (metric) => {
        if (isControlled) {
            onMetricAdd(metric);
        } else {
            setLocalSelectedMetrics(prev => [...prev, metric]);
        }
    }, [isControlled, onMetricAdd]);

    const handleMetricRemove = useMemo(() => (metricId) => {
        if (isControlled) {
            onMetricRemove(metricId);
        } else {
            setLocalSelectedMetrics(prev => prev.filter(m => m.id !== metricId));
        }
    }, [isControlled, onMetricRemove]);

    const handleMetricToggleAverage = useMemo(() => (metricId) => {
        if (isControlled && onMetricToggleAverage) {
            onMetricToggleAverage(metricId);
        } else {
            setLocalSelectedMetrics(prev => prev.map(m =>
                m.id === metricId ? { ...m, showAverage: !m.showAverage } : m
            ));
        }
    }, [isControlled, onMetricToggleAverage]);

    const handleGranularityChange = useMemo(() => (newGranularity) => {
        if (isControlled && onGranularityChange) {
            onGranularityChange(newGranularity);
        } else {
            setLocalGranularity(newGranularity);
        }
    }, [isControlled, onGranularityChange]);

    // Memoized data processing
    const processedData = useMemo(() => {
        const usingVolume = selectedMetrics.length === 0;

        if (usingVolume) {
            return data.reduce((acc, item) => {
                let key;
                let date;
                try {
                    date = typeof item.date === 'string' ? parseISO(item.date) : item.date;
                } catch (error) {
                    // If parsing fails, try using the date directly
                    date = item.date instanceof Date ? item.date : new Date();
                }

                switch (granularity) {
                    case 'week':
                        key = format(startOfWeek(date), 'yyyy-MM-dd');
                        break;
                    case 'month':
                        key = format(startOfMonth(date), 'yyyy-MM');
                        break;
                    case 'all':
                        key = 'all';
                        break;
                    default: // day
                        key = format(startOfDay(date), 'yyyy-MM-dd');
                }
                if (!acc[key]) {
                    acc[key] = { date: key, count: 0 };
                }
                acc[key].count++;
                return acc;
            }, {});
        } else {
            return aggregateDataByGranularity(data, granularity, selectedMetrics);
        }
    }, [data, granularity, selectedMetrics]);

    const formattedData = useMemo(() => {
        return Object.values(processedData).sort((a, b) =>
            new Date(a.date) - new Date(b.date)
        );
    }, [processedData]);

    const usingVolume = selectedMetrics.length === 0;

    // Calculate averages
    const metricAverages = useMemo(() => {
        //if (usingVolume) return {};
        return calculateAverages(data, selectedMetrics);
    }, [data, selectedMetrics, usingVolume]);

    // Memoize chart data
    const chartData = useMemo(() => {
        return {
            labels: formattedData.map(d => d.date),
            datasets: usingVolume
                ? [
                    // Call volume data
                    {
                        label: 'Call Volume',
                        data: formattedData.map(d => d.count),
                        borderColor: chartColors[0],
                        backgroundColor: chartColors[0],
                        tension: 0.4,
                        borderWidth: 2,
                        pointRadius: 3,
                        pointHoverRadius: 5
                    },
                    // Add average line for call volume IF showAllAverages is true
                    ...(showAllAverages ? [{
                        label: 'Call Volume Average',
                        data: Array(formattedData.length).fill(metricAverages['volume'] || 0), // Ensure we have a value
                        borderColor: chartColors[0],
                        borderDash: [5, 5],
                        tension: 0,
                        borderWidth: 1,
                        pointRadius: 0
                    }] : [])
                ]
                : [
                    // Normal metrics data
                    ...selectedMetrics.map((metric, index) => ({
                        label: metric.label,
                        data: formattedData.map(d => d[metric.id]),
                        borderColor: chartColors[index % chartColors.length],
                        backgroundColor: chartColors[index % chartColors.length],
                        tension: 0.4,
                        borderWidth: 2,
                        pointRadius: 3,
                        pointHoverRadius: 5
                    })),
                    // Average lines for selected metrics - ONLY if showAverage is true or showAllAverages is true
                    ...selectedMetrics
                        .filter(m => (m.showAverage === true || showAllAverages === true))
                        .map((metric, index) => ({
                            label: `${metric.label} Average`,
                            data: Array(formattedData.length).fill(metricAverages[metric.id] || 0), // Ensure we have a value
                            borderColor: chartColors[index % chartColors.length],
                            borderDash: [5, 5],
                            tension: 0,
                            borderWidth: 1,
                            pointRadius: 0
                        }))
                ]
        };
    }, [formattedData, selectedMetrics, metricAverages, usingVolume, showAllAverages]);

    // Memoize chart options
    const chartOptions = useMemo(() => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 0 // Disable animation to improve performance
            },
            interaction: {
                intersect: false,
                mode: 'index'
            },
            scales: {
                y: {
                    beginAtZero: true,
                    grid: {
                        color: 'rgba(0,0,0,0.05)'
                    }
                },
                x: {
                    grid: {
                        color: 'rgba(0,0,0,0.05)'
                    }
                }
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: { size: 12 }
                    }
                }
            }
        };
    }, []);

    // Update chart only when needed
    useEffect(() => {
        chartUpdateNeeded.current = true;
    }, [chartData]);

    // Debounced chart update to prevent rapid re-renders
    useEffect(() => {
        if (!chartUpdateNeeded.current) return;

        let animationFrameId = null;

        const updateChart = () => {
            if (!chartRef.current) return;

            try {
                if (chartInstance.current) {
                    chartInstance.current.destroy();
                }

                const ctx = chartRef.current.getContext('2d');
                if (!ctx) return;

                chartInstance.current = new Chart(ctx, {
                    type: 'line',
                    data: chartData,
                    options: chartOptions
                });

                chartUpdateNeeded.current = false;
            } catch (error) {
                console.error("Error updating chart:", error);
            }
        };

        // Delay chart update to avoid frequent re-renders
        const timeoutId = setTimeout(() => {
            animationFrameId = requestAnimationFrame(updateChart);
        }, 50);

        return () => {
            clearTimeout(timeoutId);
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [chartData, chartOptions]);

    // Clean up chart on unmount
    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                try {
                    chartInstance.current.destroy();
                } catch (error) {
                    console.error("Error destroying chart:", error);
                }
            }
        };
    }, []);

    // Handle show all averages toggle
    const handleAveragesToggle = useMemo(() => (checked) => {
        setShowAllAverages(checked);

        if (isControlled && onMetricToggleAverage) {
            selectedMetrics.forEach(metric => {
                // Only update if current state doesn't match desired state
                if ((metric.showAverage || false) !== checked) {
                    onMetricToggleAverage(metric.id);
                }
            });
        } else {
            setLocalSelectedMetrics(prev => prev.map(metric => ({
                ...metric,
                showAverage: checked
            })));
        }
    }, [selectedMetrics, isControlled, onMetricToggleAverage]);
    //console.log("Rendering ChartSection");
    return (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
            }}>
                <MetricSelector
                    availableMetrics={availableMetrics}
                    selectedMetrics={selectedMetrics}
                    onMetricAdd={handleMetricAdd}
                    onMetricRemove={handleMetricRemove}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>Show Averages</span>
                        <Switch
                            checked={showAllAverages}
                            onChange={handleAveragesToggle}
                            style={{ backgroundColor: showAllAverages ? '#10b981' : undefined }}
                        />
                    </div>
                    <TimeGranularity
                        value={granularity}
                        onChange={handleGranularityChange}
                    />
                </div>
            </div>
            <div style={{ height: 400, width: '100%' }}>
                <canvas ref={chartRef} />
            </div>
        </div>
    );
});

// Set display name for debugging
ChartSection.displayName = 'ChartSection';

export default ChartSection;
